import { ModuleAForm01Values, ReasonForInspectionOption } from '@moduleAForm';
import {
	CertificateItem,
	CreateCertificatePayload,
	MCCSummaryItem,
	ReasonForInspection,
} from '@types';
import { hasOutcomeOnMCC } from '@utils/CertificateHelper';

import { Mapper } from './Mapper';

export class CertificateCreationMapper extends Mapper<
	ModuleAForm01Values,
	CertificateItem,
	CreateCertificatePayload
> {
	private mccSummaryData: MCCSummaryItem;

	constructor(mccSummaryData: MCCSummaryItem) {
		super();
		this.mccSummaryData = mccSummaryData;
	}

	mapToForm(data: CertificateItem): ModuleAForm01Values {
		const isReplacement = data.reasonForInspection === ReasonForInspection.REPLACEMENT; // prettier-ignore
		return {
			field1: data.meterCaseCode,
			field2: this.transformReasonForInspectionToIn(data.reasonForInspection, isReplacement),
			field3: isReplacement,
			field4: true,
			field5: undefined,
			field6: undefined,
		};
	}

	mapFormToPayload(formValues: ModuleAForm01Values): CreateCertificatePayload {
		const isForNewMeter = formValues.field2 === ReasonForInspectionOption.NewMeter;
		const hasOutcome = hasOutcomeOnMCC(this.mccSummaryData);
		const numberOfNewMCCs = this.convertPossibleStringToNumber(formValues.field6);

		// needs new MC generated and new MDC Form generated
		const needsGenerateSingleNewMC = hasOutcome && isForNewMeter; // 8.2.1
		const needsGenerateMultipleNewMCs = !hasOutcome && isForNewMeter && numberOfNewMCCs; // 5.1.1

		const payload: CreateCertificatePayload = {
			meterCaseCode: formValues.field1,
			reasonForInspection: this.transformReasonForInspectionToOut(formValues),
			createForThisMCC: true,
		};

		// adjust payload based on new MCs to generate
		if (needsGenerateSingleNewMC) {
			payload.createForThisMCC = false;
			payload.numberOfNewMCCs = 1;
		} else if (needsGenerateMultipleNewMCs) {
			payload.numberOfNewMCCs = numberOfNewMCCs;
		}

		return payload;
	}

	private transformReasonForInspectionToIn(reason: ReasonForInspection, isReplacement: boolean) {
		return isReplacement
			? ReasonForInspectionOption.NewMeter
			: (reason as unknown as ReasonForInspectionOption);
	}

	private transformReasonForInspectionToOut(formValues: ModuleAForm01Values) {
		const reason = formValues.field2;
		const isReplacement = formValues.field3;

		return isReplacement
			? ReasonForInspection.REPLACEMENT
			: (reason as unknown as ReasonForInspection);
	}

	private convertPossibleStringToNumber(value: string | number | undefined) {
		return typeof value === 'string' ? Number(value) : value;
	}
}
